import { Input } from "igfd-ui";

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Box, Fade, Typography } from "@mui/material";

import { CheckCircleRounded } from "@mui/icons-material";

import {
  type RestaurantReducerDispatch,
  updateRestaurant,
} from "@dashboard/store/actions/restaurantActions";
import { ReduxState } from "@dashboard/types/reduxState";

import { AutosaveStatus, useAutosave } from "../autosave/Autosave";

export const cleanWebsiteField = (rawText: string) => {
  try {
    const url = new URL(rawText);
    return url.hostname;
  } catch (e) {
    return null;
  }
};

export const WebsiteInputComponent = () => {
  const [pastedSuccessfully, setPastedSuccessfully] = useState(false);
  useEffect(() => {
    // reset the pastedSuccessfully state after 3 seconds
    if (pastedSuccessfully) {
      setTimeout(() => {
        setPastedSuccessfully(false);
      }, 3000);
    }
  }, [pastedSuccessfully]);

  const initialWebsite = useSelector(
    (state: ReduxState) => state.restaurant.selectedRestaurant!.website || ""
  );

  const dispatch: RestaurantReducerDispatch = useDispatch();
  const setWebsite = (website: string) =>
    dispatch(updateRestaurant({ website }));

  const { inputProps, saved, onClick } = useAutosave({
    initialData: initialWebsite,
    saveCallback: setWebsite,
  });

  return (
    <Input
      id="website"
      label="Website"
      {...inputProps}
      endAdornment={
        <Box>
          <Fade
            in={pastedSuccessfully}
            // disable enter animation
            easing={{ enter: "none" }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
                paddingLeft: 1,
              }}
            >
              <CheckCircleRounded
                sx={({ palette }) => ({ color: palette.primary.main })}
                fontSize={"small"}
              />
              <Typography variant={"caption"} textAlign={"center"}>
                Parsed
              </Typography>
            </Box>
          </Fade>
          <AutosaveStatus saved={saved} />
        </Box>
      }
      onPaste={(event) => {
        // when pasted, we try to parse the website and extract the domain from the
        // pasted text. If we fail we just use the pasted text as is.
        const pastedText = event.clipboardData.getData("text");
        const cleanedText = cleanWebsiteField(pastedText);
        if (cleanedText) {
          onClick(cleanedText);
          setPastedSuccessfully(true);
        } else {
          onClick(pastedText);
        }
      }}
      onChange={(event) => {
        // only set if the event is not a paste event
        // @ts-ignore
        if (event.nativeEvent.inputType !== "insertFromPaste") {
          inputProps.onChange(event);
        }
      }}
      type="text"
      placeholder="Enter or paste website"
      fullWidth
    />
  );
};
